import {
  EnumerationDto,
  EnumerationType,
  FollowUpsDto,
  ProjectResponseDto
} from '../../build/generated-sources/openapi';
import {
  stringDateTimeToFormattedString,
  formatNumberWithCurrency,
  stringDateToFormattedString,
  truncateString
} from '../../core/util/Util';
import { MRT_Cell } from 'material-react-table';
import { Link } from 'react-router-dom';
import { UserSelectType } from './Projects.model';
import Typography from '@mui/material/Typography';
import { Tooltip } from '@mantine/core';

export const getProjectsColumns = (
  usersList: UserSelectType[],
  canSeeUsers: boolean,
  enumerations: EnumerationDto[],
  followUps: FollowUpsDto
): any => {
  return [
    {
      accessorKey: 'registrationNumber',
      enableClickToCopy: true,
      header: 'Číslo projektu',
      size: 180
    },
    {
      id: 'projectLink',
      size: 170,
      accessorFn: (row: ProjectResponseDto) => `CisloProjektu_${row.registrationNumber}`,
      header: 'Název',
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: false,
      Cell: ({ cell }: { cell: MRT_Cell<any> }, row: any) => {
        return (
          <Link to={`/projekty/${cell.row.original.id}`} relative="path">
            {`CisloProjektu_${cell.row.original.registrationNumber}`}
          </Link>
        );
      }
    },
    {
      accessorKey: 'name',
      size: 260,
      enableClickToCopy: true,
      header: 'Název projektu'
    },
    {
      accessorKey: 'holder',
      size: 280,
      enableClickToCopy: true,
      header: 'Název nositele projektu'
    },
    {
      accessorKey: 'holderIc',
      size: 180,
      enableClickToCopy: true,
      header: 'IČ Nositele'
    },
    {
      accessorKey: 'holderLegalForm',
      size: 190,
      enableClickToCopy: true,
      header: 'Právní forma nositele',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.HolderLegalForm)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      accessorKey: 'createdBy',
      size: 160,
      accessorFn: (row: ProjectResponseDto) => row,
      header: 'Autor',
      enableColumnFilter: canSeeUsers,
      filterSelectOptions: usersList,
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<ProjectResponseDto> }) => {
        const project = cell.getValue() as ProjectResponseDto;
        if (project.createdBy) {
          return project.createdBy.firstName + ' ' + project.createdBy.lastName;
        } else if (project.createdByLegacy) {
          return project.createdByLegacy;
        }
        return '';
      }
    },
    {
      accessorKey: 'updatedBy',
      size: 160,
      accessorFn: (row: ProjectResponseDto) => row,
      header: 'Autor změny',
      enableColumnFilter: canSeeUsers,
      filterSelectOptions: usersList,
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<ProjectResponseDto> }) => {
        const project = cell.getValue() as ProjectResponseDto;
        if (project.updatedBy) {
          return project.updatedBy.firstName + ' ' + project.updatedBy.lastName;
        } else if (project.updatedByLegacy) {
          return project.updatedByLegacy;
        }
        return '';
      }
    },
    {
      // [DAP-030] Přiřazený řešitel — filtrovatelný/vyhledatelný sloupec (multi-select uživatelů,
      // stejně jako Autor / Autor změny). Filtr posílá keycloakId; backend filtruje přes assignedTo.
      accessorKey: 'assignedTo',
      size: 160,
      accessorFn: (row: ProjectResponseDto) => row,
      header: 'Přiřazený řešitel',
      enableColumnFilter: canSeeUsers,
      filterSelectOptions: usersList,
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<ProjectResponseDto> }) => {
        const project = cell.getValue() as ProjectResponseDto;
        if (project.assignedTo) {
          return project.assignedTo.firstName + ' ' + project.assignedTo.lastName;
        }
        return '';
      }
    },
    {
      accessorKey: 'eventNumber',
      size: 150,
      enableClickToCopy: true,
      header: 'Číslo akce'
    },
    {
      accessorKey: 'guarantor',
      size: 180,
      enableClickToCopy: true,
      header: 'Garant projektu'
    },
    {
      accessorKey: 'contactPerson',
      size: 190,
      enableClickToCopy: true,
      header: 'Kontaktní osoba/tel'
    },
    {
      accessorKey: 'contactEmails',
      size: 260,
      enableClickToCopy: true,
      header: 'Kontaktní email',
      Cell: ({ cell }: { cell: MRT_Cell<ProjectResponseDto> }) => {
        if (!cell.getValue()) {
          return '';
        }
        const emails = (cell.getValue() as string).split(';').map((value) => ({ value }));
        return (
          <>
            {emails &&
              emails.length != 0 &&
              emails.map((email, index) => {
                return (
                  <Typography key={index} variant={'body2'}>
                    {truncateString(email.value)}
                  </Typography>
                );
              })}
          </>
        );
      }
    },
    {
      accessorKey: 'manager',
      size: 180,
      enableClickToCopy: true,
      header: 'Vedoucí projektu'
    },
    {
      accessorKey: 'briefDescription',
      size: 260,
      columnResizeDirection: 'rtl',
      header: 'Stručný popis projektu',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (
          <Tooltip multiline w={220} color={'dpkk-blue'} label={cell.getValue() as string}>
            <Typography variant={'body2'}>
              {truncateString(cell.getValue() as string, 30)}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      accessorKey: 'totalCosts',
      size: 230,
      enableClickToCopy: true,
      header: 'Celkové náklady dle finančního příslibu',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return formatNumberWithCurrency(cell.getValue() as number);
      }
    },
    {
      accessorKey: 'result',
      size: 260,
      enableClickToCopy: true,
      header: 'Výsledek/výstup projektu'
    },
    {
      accessorKey: 'resultQuantification',
      size: 210,
      enableClickToCopy: true,
      header: 'Kvantifikace výsledku'
    },
    {
      accessorKey: 'type',
      size: 180,
      enableClickToCopy: true,
      header: 'Typ projektu',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.ProjectType)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      accessorKey: 'partners',
      size: 230,
      enableClickToCopy: true,
      header: 'Spolupracující subjekt'
    },
    {
      accessorKey: 'realizationPlace',
      size: 190,
      enableClickToCopy: true,
      header: 'Místo realizace'
    },
    {
      accessorKey: 'followUpPrkkPriorityArea',
      size: 260,
      accessorFn: (row: ProjectResponseDto) => row,
      header: 'Návaznost na PR KK',
      filterSelectOptions: followUps?.followUpPrkkPriorityArea
        ? followUps?.followUpPrkkPriorityArea.map((entry) => {
            return { value: entry.id + '', label: entry.title };
          })
        : [],
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<ProjectResponseDto> }) => {
        const project = cell.getValue() as ProjectResponseDto;
        return (
          <>
            {project.followUpPrkkPriorityArea && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpPrkkPriorityArea?.title)}
              </Typography>
            )}
            {project.followUpPrkkSubregion && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpPrkkSubregion?.title)}
              </Typography>
            )}
            {project.followUpPrkkSpecificTarget && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpPrkkSpecificTarget?.title)}
              </Typography>
            )}
            {project.followUpPrkkMeasure && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpPrkkMeasure?.title)}
              </Typography>
            )}
          </>
        );
      }
    },
    {
      accessorKey: 'followUpSrrcrStrategicTarget',
      size: 260,
      accessorFn: (row: ProjectResponseDto) => row,
      header: 'Návaznost na SRR ČR',
      filterSelectOptions: followUps?.followUpSrrcrStrategicTarget
        ? followUps?.followUpSrrcrStrategicTarget.map((entry) => {
            return { value: entry.id + '', label: entry.title };
          })
        : [],
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<ProjectResponseDto> }) => {
        const project = cell.getValue() as ProjectResponseDto;
        return (
          <>
            {project.followUpSrrcrStrategicTarget && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpSrrcrStrategicTarget?.title)}
              </Typography>
            )}
            {project.followUpSrrcrSpecificTarget && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpSrrcrSpecificTarget?.title)}
              </Typography>
            )}
            {project.followUpSrrcrTypeMeasure && (
              <Typography variant={'body2'}>
                {truncateString(project?.followUpSrrcrTypeMeasure?.title)}
              </Typography>
            )}
          </>
        );
      }
    },
    {
      accessorKey: 'actionPlan',
      size: 170,
      header: 'Akční plány',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.ActionPlan)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      accessorKey: 'developmentState',
      size: 270,
      header: 'Stav rozpracovanosti navrhovaného projektu',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.DevelopmentState)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      accessorKey: 'legalAct',
      header: 'Právní akt (rozhodnutí)',
      filterSelectOptions: [
        { value: 'true', label: 'Ano' },
        { value: 'false', label: 'Ne' }
      ],
      filterVariant: 'select',
      size: 210,
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return cell.getValue() === true ? 'Ano' : 'Ne';
      }
    },
    {
      // datepicker – [DAP-021] Datum vydání právního aktu (filtr date-range + řazení jako
      // ostatní datumové sloupce); v seznamu volitelný (default skrytý, viz DEFAULT_COLUMN_VISIBILITY).
      accessorKey: 'legalActDate',
      accessorFn: (row: ProjectResponseDto) => `${stringDateToFormattedString(row.legalActDate)}`,
      header: 'Datum vydání právního aktu',
      size: 250,
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      accessorKey: 'initiative',
      size: 190,
      header: 'OP nebo iniciativa',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.Initiative)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      // datepicker
      accessorKey: 'preprojectPreparationStart',
      size: 230,
      accessorFn: (row: ProjectResponseDto) =>
        `${stringDateToFormattedString(row.preprojectPreparationStart)}`,
      header: 'Zahájení předprojektové přípravy',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      // datepicker
      accessorKey: 'implementationStart',
      size: 190,
      accessorFn: (row: ProjectResponseDto) =>
        `${stringDateToFormattedString(row.implementationStart)}`,
      header: 'Zahájení realizace',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      // datepicker
      accessorKey: 'implementationEnd',
      size: 190,
      accessorFn: (row: ProjectResponseDto) =>
        `${stringDateToFormattedString(row.implementationEnd)}`,
      enableClickToCopy: true,
      header: 'Ukončení realizace',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      accessorKey: 'implementationLength',
      size: 220,
      enableClickToCopy: true,
      header: 'Délka fyzické realizace - v měsících'
    },
    {
      // datepicker
      accessorKey: 'financialEnd',
      size: 180,
      accessorFn: (row: ProjectResponseDto) => `${stringDateToFormattedString(row.financialEnd)}`,
      header: 'Finanční ukončení projektu',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      // datepicker
      accessorKey: 'updated',
      size: 170,
      accessorFn: (row: ProjectResponseDto) => `${stringDateTimeToFormattedString(row.updated)}`,
      enableClickToCopy: true,
      header: 'Změněno',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      // datepicker
      accessorKey: 'sustainabilityPeriodStart',
      size: 220,
      accessorFn: (row: ProjectResponseDto) =>
        `${stringDateToFormattedString(row.sustainabilityPeriodStart)}`,
      header: 'Zahajeni doby udrzitelnosti',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      // datepicker
      accessorKey: 'sustainabilityPeriodEnd',
      size: 220,
      accessorFn: (row: ProjectResponseDto) =>
        `${stringDateToFormattedString(row.sustainabilityPeriodEnd)}`,
      header: 'Ukončení doby udrzitelnosti',
      filterVariant: 'date-range',
      muiFilterDatePickerProps: {
        timezone: 'Europe/Prague'
      }
    },
    {
      accessorKey: 'territorialDecision',
      size: 190,
      header: 'Uzemni rozhodnuti',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.Decision)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      accessorKey: 'buildingPermit',
      size: 180,
      header: 'Stavebni povoleni',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.Decision)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    },
    {
      accessorKey: 'totalEstimatedCosts',
      size: 240,
      enableClickToCopy: true,
      header: 'Celkové (předpokládané) náklady projektu',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return formatNumberWithCurrency(cell.getValue() as number);
      }
    },
    {
      accessorKey: 'preparationCosts',
      size: 190,
      enableClickToCopy: true,
      header: 'Náklady na přípravu projektu',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return formatNumberWithCurrency(cell.getValue() as number);
      }
    },
    {
      accessorKey: 'implementationCosts',
      size: 190,
      enableClickToCopy: true,
      header: 'Náklady na realizaci projektu',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return formatNumberWithCurrency(cell.getValue() as number);
      }
    },
    {
      accessorKey: 'priority',
      header: 'Priorita',
      filterSelectOptions: [
        { value: 'true', label: 'Ano' },
        { value: 'false', label: 'Ne' }
      ],
      filterVariant: 'select',
      size: 180,
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return cell.getValue() === true ? 'Ano' : 'Ne';
      }
    },
    {
      accessorKey: 'fundingMainSource',
      size: 220,
      header: 'Předpokládaný hlavní zdroj financování',
      filterSelectOptions: enumerations
        ?.filter((enumeration) => enumeration.type === EnumerationType.FundingMainSource)
        .map((entry) => {
          return { value: entry.name, label: entry.translation };
        }),
      filterVariant: 'multi-select',
      Cell: ({ cell }: { cell: MRT_Cell<any> }) => {
        return (cell.getValue() as EnumerationDto)?.translation;
      }
    }
  ];
};
